import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import type { ResponseDataRows } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';
import type { Dispatch, ReactElement, SetStateAction } from 'react';

import { ButtonPopover } from '@common/ButtonPopover';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { confirmDialogStore } from '@stores/confirmDialog';
import { constantStore } from '@stores/constantStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';
import { userStore } from '@stores/userStore';
import type { ReportingBlockDto, ReportingPackageDto } from '@src/api';

import type { UploadParamsType } from '../PublicationRegister';

import styles from '../PublicationRegister.module.scss';

interface IActionButton {
  toggleAddPackageDrawer: () => void;
  toggleAddReportDrawer: () => void;
  toggleUploadModal: () => void;
  toggleRejectModal: () => void;
  toggleDownloadReportExcelModal: () => void;
  setUploadParams: Dispatch<SetStateAction<UploadParamsType | undefined>>;
  data: ResponseDataRows;
  update: () => void;
}

const { CREATE, PUBLISHED, FOR_APPROVAL, NEEDS_IMPROVEMENT, PREPARED } = constantStore.REPORT_PACKAGE_STATUS;

export const ActionButton = observer(
  ({
    toggleAddPackageDrawer,
    toggleAddReportDrawer,
    toggleUploadModal,
    toggleRejectModal,
    toggleDownloadReportExcelModal,
    setUploadParams,
    data,
    update,
  }: IActionButton): ReactElement => {
    const {
      setPackageFormInitials,
      getExcelStream,
      getBlankExcel,
      deletePackage,
      updateReportPackageStatus,
      cleanReport,
      cleanReportPackage,
      calculateRegisters,
      returnReportPackageToWork,
      setRejectModal,
      setDownloadReports,
      organizationCode,
      selected,
      updateReportPublish,
      sendReportPackageOnApprove,
    } = publicationRegisterStore;
    const { user } = userStore;
    const { selectRows, rows = [] } = selected || {};

    const { push } = useHistory();

    const cmReportPackagePermission = usePermission('CmReportPackage');
    const cmReportPackageStatusPermission = usePermission('CmReportPackageStatus');
    const cmCalculationRegisterPermission = usePermission('CmCalculationRegister');

    const onCopy = useCallback(
      (packageInfo) => () => {
        setPackageFormInitials({
          reportPackageCode: packageInfo.reportPackageCode,
          reportGroupCode: packageInfo.reportGroupCode,
          year: `${packageInfo.reportPackageYear}`,
          correction: packageInfo.reportPackageCorrection,
          periodCode: packageInfo.periodCode,
          organizationCode: packageInfo.organization,
          name: packageInfo.reportPackageName,
        });
        toggleAddPackageDrawer();
      },
      [setPackageFormInitials, toggleAddPackageDrawer],
    );

    const onUpload = useCallback(
      (uploadType) => {
        setUploadParams({
          uploadType,
          organizationName: rows[0].organizationCode + '',
          reportPackageCode: rows[0].reportPackageCode + '',
          reportVersionCode: rows[0].reportVersionCode + '',
        });
        toggleUploadModal();
      },
      [rows, setUploadParams, toggleUploadModal],
    );

    const onDownloadExcel = useCallback(
      (isOnePackageSelect, isBlank) => {
        if (isOnePackageSelect) {
          setDownloadReports({ reports: rows ?? [], downloadType: isBlank ? 'BLANK_XLSX' : 'XLSX' });
          toggleDownloadReportExcelModal();
          return;
        }

        const onDownload = () => {
          const out: { organizationCode: string; reportPackageCode: string; reportVersionCode: string }[] = rows.map(
            (item) => ({
              organizationCode: item.organizationCode as string,
              reportPackageCode: item.reportPackageCode as string,
              reportVersionCode: item.reportVersionCode as string,
            }),
          );

          if (isBlank) {
            getBlankExcel(out);
          } else {
            getExcelStream(out);
          }
        };

        if (rows.length > 100) {
          confirmDialogStore.open({
            text: 'Вы пытаетесь выгрузить больше 100 файлов. Продолжить?',
            onOk: () => {
              onDownload();
            },
          });
        } else {
          onDownload();
        }
      },
      [getBlankExcel, getExcelStream, rows, setDownloadReports, toggleDownloadReportExcelModal],
    );

    const onDelete = useCallback(() => {
      const warning = rows.some((report: ReportingBlockDto) => report.reportPublishStatus !== null);

      confirmDialogStore.open({
        text: (
          <>
            Удалить выбранный элемент? <br />
            {warning ? 'Все данные в загруженных отчетах будут удалены' : ''}
          </>
        ),
        onOk: () => {
          deletePackage(rows[0].reportPackageCode + '');
        },
      });
    }, [deletePackage, rows]);

    const onUpdateReportPublish = useCallback(async () => {
      await updateReportPublish({ ...rows[0], status: NEEDS_IMPROVEMENT, username: user?.fio });
      update();
    }, [rows, update, updateReportPublish, user?.fio]);

    const onSendReportPackageOnApprove = useCallback(
      async (code) => {
        await sendReportPackageOnApprove(code);
        update();
      },
      [sendReportPackageOnApprove, update],
    );

    const onUpdateStatus = useCallback(
      async (code, status, reports?) => {
        const result = await updateReportPackageStatus(code, status);

        if (result === 'Невозможно отклонить пакет') {
          setRejectModal({ packageCode: code, reports });
          toggleRejectModal();
          return;
        }

        if (result) {
          confirmDialogStore.open({
            width: 600,
            text: (
              <>
                В отчете нет данных. Опубликовать пакет? <br />
                <ul className={styles.list}>
                  {Array.isArray(result) && result.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </>
            ),
            onOk: () => {
              updateReportPackageStatus(code, status, true);
              update();
            },
          });
        }

        if (!result) update();
      },
      [setRejectModal, toggleRejectModal, update, updateReportPackageStatus],
    );

    const onReturnReportPackageToWork = useCallback(
      async (code) => {
        await returnReportPackageToWork(code);
        update();
      },
      [returnReportPackageToWork, update],
    );

    const onCreate = useCallback(
      (isCreatePackage) => {
        if (isCreatePackage) {
          toggleAddPackageDrawer();
        } else {
          toggleAddReportDrawer();
        }
      },
      [toggleAddPackageDrawer, toggleAddReportDrawer],
    );

    const showNotification = useCallback(
      (text, guid, etlTypeId) => {
        const onGo = () => {
          push(RoutePath.LOGS, { guid, etlTypeId });
          Notification.close('showNotification');
        };

        Notification({
          message: (
            <>
              {text}{' '}
              <span onClick={onGo} className={styles.link}>
                Мониторинг фоновых процессов
              </span>
            </>
          ),
          type: 'success',
          key: 'showNotification',
        });
      },
      [push],
    );

    const onCalculateRegisters = useCallback(async () => {
      // берем reportVersionCode из выделенных отчетов
      const reportVersionCodes = rows.map((item) => item.reportVersionCode + '');

      const result = await calculateRegisters(rows[0].reportPackageCode + '', reportVersionCodes);

      if (result)
        showNotification('Запущен расчет данных. Результат будет отображён в разделе', result.guid, result.etlTypeId);
    }, [calculateRegisters, rows, showNotification]);

    const isOnePackageSelect = selectRows?.selectedGroups?.length === 1 && selectRows?.selectedGroups[0].length === 4;

    const isOneReportSelect = selectRows?.selectedRowKeys?.length === 1;

    const isOneRegisterSelect = selectRows?.selectedGroups?.length === 1 && selectRows?.selectedGroups[0].length === 5;

    const packageInfo = (
      rows.length
        ? rows[0]?.packageInfo
        : data?.find((item) => item.reportPackageCode === selectRows?.selectedGroups?.[0]?.at(-1))?.packageInfo ?? {}
    ) as ReportingPackageDto;

    const entityUser = isOnePackageSelect ? packageInfo?.reportPackageUser : rows[0]?.reportPublishUser;

    const status = isOnePackageSelect ? packageInfo?.reportPackageStatus : rows[0]?.reportPublishStatus;

    const disabledForApprovalBtn = status !== CREATE && status !== NEEDS_IMPROVEMENT; // на "На утверждение" доступна для статусов "Создан" и "Требует доработки"
    const disabledRequiresImprovementBtn = status !== PREPARED; // "Требует доработки" для отчетов, активна только для статуса "Подготовлен"
    const disabledRejectBtn = status !== FOR_APPROVAL; // "Отклонить" для пакетов, активна только для статуса "На утверждении"
    const disabledPublishBtn = status !== FOR_APPROVAL; // "Опубликовать" неактивна для всех статусов пакета, кроме статуса "На утверждении"
    const disabledClearBtn = isOnePackageSelect ? status !== CREATE : status !== PREPARED; // доступна и для пакета в статусе "Создан", и для отчета только в статусе "Подготовлен"
    const curatorDisabled =
      (isOnePackageSelect ? status === FOR_APPROVAL : status === PREPARED) && entityUser !== user?.fio; // при статусе пакета "На утверждении" (у отчета "Подготовлен"), кнопки загрузить, очистить, удалить, становятся недоступны для пользователей, кроме куратора
    const disabledForApproved = status === PUBLISHED; // при статусе "Утвержден", становятся недоступны кнопки загрузить, очистить, удалить, на утверждение, подготовлен, отклонить, требует доработки, опубликовать

    // может быть выбрал либо один пакет, либо несколько отчетов из одного пакета
    const disableTransferData = useCallback(() => {
      if (isOneRegisterSelect && !isOnePackageSelect) return false;

      if (rows.length > 0 && !isOnePackageSelect) {
        const reportPackageCode = rows[0]?.reportPackageCode;
        const reportTypeCode = rows[0]?.reportTypeCode;
        if (
          rows.every(
            (item: ReportingBlockDto) =>
              item.reportPackageCode === reportPackageCode &&
              item.reportTypeCode === reportTypeCode &&
              item.reportVersionCode,
          )
        )
          return false;
      }

      return true;
    }, [isOnePackageSelect, isOneRegisterSelect, rows]);

    const createBtn = [
      {
        name: 'Создать пакет',
        title: 'Создать пакет',
        onClick: () => onCreate(true),
      },
      {
        name: 'Добавить отчет в пакет',
        title: 'Добавить отчет в пакет',
        onClick: () => onCreate(false),
      },
    ];

    const statusBtn = [
      {
        name: 'На утверждение',
        title: 'На утверждение',
        disabled: !isOnePackageSelect || disabledForApprovalBtn || !cmReportPackageStatusPermission.CREATE,
        onClick: () => onSendReportPackageOnApprove(packageInfo.reportPackageCode),
      },
      {
        name: 'Требует доработки',
        title: 'Требует доработки',
        disabled: !isOneReportSelect || disabledRequiresImprovementBtn || !cmReportPackageStatusPermission.UPDATE,
        onClick: () => onUpdateReportPublish(),
      },
      {
        name: 'Отклонить',
        title: 'Отклонить',
        disabled: !isOnePackageSelect || disabledRejectBtn || !cmReportPackageStatusPermission.UPDATE,
        onClick: () => onUpdateStatus(packageInfo.reportPackageCode, NEEDS_IMPROVEMENT, rows || []),
      },
      {
        name: 'Утвердить',
        title: 'Утвердить',
        disabled: !isOnePackageSelect || disabledPublishBtn || !cmReportPackageStatusPermission.UPDATE,
        onClick: () => onUpdateStatus(packageInfo.reportPackageCode, PUBLISHED),
      },
      {
        name: 'Вернуть в работу',
        title: 'Вернуть в работу',
        disabled: !isOnePackageSelect || status !== PUBLISHED || !cmReportPackageStatusPermission.UPDATE,
        onClick: () => onReturnReportPackageToWork(packageInfo.reportPackageCode),
      },
      {
        name: 'Очистить',
        title: 'Очистить',
        disabled: disabledClearBtn || curatorDisabled || !cmReportPackagePermission.UPDATE,
        onClick: () =>
          isOneReportSelect
            ? cleanReport(rows[0].reportPackageCode + '', rows[0].reportVersionCode + '')
            : cleanReportPackage(packageInfo.reportPackageCode + ''),
      },
    ];

    return (
      <>
        <ButtonPopover items={createBtn} key="createCustomButton">
          <IconButton
            key="create"
            iconName="add"
            title="Добавить"
            disabled={!organizationCode || !cmReportPackagePermission.CREATE}
          />
        </ButtonPopover>

        <IconButton
          key="copy"
          disabled={!isOnePackageSelect || !cmReportPackagePermission.CREATE}
          onClick={onCopy(packageInfo)}
          iconName="copy"
          title="Копировать"
        />
        <IconButton
          key="delete"
          disabled={!isOnePackageSelect || curatorDisabled || disabledForApproved || !cmReportPackagePermission.DELETE}
          onClick={onDelete}
          iconName="delete"
          title="Удалить"
        />

        <ButtonPopover
          items={[
            {
              name: 'XLSX',
              onClick: () => onUpload('excel'),
              title: 'Excel',
              disabled: !isOneReportSelect || curatorDisabled || disabledForApproved,
            },
          ]}
          key="uploadMenu"
        >
          <IconButton iconName="upload" title="Загрузить" disabled={!cmCalculationRegisterPermission.CREATE} />
        </ButtonPopover>

        <ButtonPopover
          items={[
            {
              name: 'XLSX',
              onClick: () => onDownloadExcel(isOnePackageSelect, false),
              title: 'Excel',
              disabled: rows.length === 0,
            },
            {
              name: 'BLANK_XLSX',
              onClick: () => onDownloadExcel(isOnePackageSelect, true),
              title: 'Пустой шаблон',
              disabled: rows.length === 0,
            },
          ]}
          key="downloadMenu"
        >
          <IconButton iconName="download" title="Скачать" />
        </ButtonPopover>

        <IconButton
          key="calc"
          iconName="postAdd"
          title="Выполнить расчет"
          disabled={disableTransferData() || !cmCalculationRegisterPermission.CREATE}
          onClick={onCalculateRegisters}
        />

        <ButtonPopover items={statusBtn} key="customButton">
          <IconButton
            key="downloadMenu"
            iconName="filterButton"
            title="Управление статусами пакета"
            disabled={!(isOnePackageSelect || isOneReportSelect)}
          />
        </ButtonPopover>
      </>
    );
  },
);
